import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import ByIndexDB from '@bfe/by-idb';
import { toNumber, isFunction, map } from '@bfe/by-helpers';
import logTasks from './log-tasks';
import TABLES from './logger-tables';
import http from './http'; // NOTE: Must upgrade version if you change the store schema.

var DB_VERSION = 2;
var SKYNET_DB_NAME = 'by-skynet-db';
/**
 * Client indexDB migration function
 *
 * @param {object} db The indexDB instance.
 */

var onDBUpgrade = function onDBUpgrade(db) {
  if (!db.objectStoreNames.contains(TABLES.DEFAULT_LOGGER_STORE)) {
    db.createObjectStore(TABLES.DEFAULT_LOGGER_STORE, {
      autoIncrement: true
    });
  }

  if (!db.objectStoreNames.contains(TABLES.STORE_PV)) {
    db.createObjectStore(TABLES.STORE_PV, {
      autoIncrement: true
    });
  }

  if (!db.objectStoreNames.contains(TABLES.STORE_EVENT)) {
    db.createObjectStore(TABLES.STORE_EVENT, {
      autoIncrement: true
    });
  }

  if (!db.objectStoreNames.contains(TABLES.STORE_SIGNUP)) {
    db.createObjectStore(TABLES.STORE_SIGNUP, {
      autoIncrement: true
    });
  }
};

export { TABLES as ByLoggerTables };

var ByLogger = /*#__PURE__*/function () {
  function ByLogger() {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var eventOpts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _classCallCheck(this, ByLogger);

    this.isRealtimeReport = opts.reportInterval === -1;
    this.opts = _objectSpread({
      extendFields: false,
      reportInterval: 45000,
      reportUrl: '',
      noBackgroundTasks: false,
      keepAliveInBackground: false,
      maxSendBufferSize: 80,
      logTable: TABLES.DEFAULT_LOGGER_STORE
    }, opts);
    this.eventOpts = _objectSpread({
      v: 0,
      p: 0
    }, eventOpts);
    this.tasks = []; // local params

    this.timer = 0;
    this.tabActive = true;
    this.tmpSenderSize = this.opts.maxSendBufferSize;
    this.initialized = false; // No need to initialize idb if in realtime report mode.

    this.idb = new ByIndexDB(SKYNET_DB_NAME, DB_VERSION, onDBUpgrade);
  }
  /**
   * Logger init function
   */


  _createClass(ByLogger, [{
    key: "init",
    value: function init() {
      var _this = this;

      if (typeof window === 'undefined') return;

      if (!this.opts.keepAliveInBackground) {
        document.addEventListener('visibilitychange', this.visibilityChange.bind(this));
      }

      if (!this.opts.noBackgroundTasks) {
        this.tasks = logTasks.map(function (task) {
          return task(_this.log.bind(_this), _this.opts);
        });
        this.tasks.forEach(function (task) {
          return task.start();
        });
      }

      if (!this.isRealtimeReport) {
        this.timer = setInterval(this.consume.bind(this), this.opts.reportInterval);
      }

      this.initialized = true;
    }
    /**
     * Visibility change callback
     */

  }, {
    key: "visibilityChange",
    value: function visibilityChange() {
      var _this2 = this;

      this.tabActive = !document.hidden;
      this.tasks.forEach(function (task) {
        return task.toggleVisible(_this2.tabActive);
      });
    }
    /**
     * Consume the log events
     */

  }, {
    key: "consume",
    value: function consume() {
      var _this3 = this;

      if (!this.tabActive || !this.initialized) return Promise.resolve();
      var tmpSenderSize = this.tmpSenderSize,
          isRealtimeReport = this.isRealtimeReport;
      var _this$opts = this.opts,
          maxSendBufferSize = _this$opts.maxSendBufferSize,
          logTable = _this$opts.logTable,
          reportUrl = _this$opts.reportUrl;
      return this.idb.head(logTable, tmpSenderSize).then(function () {
        var dataMap = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
        var dataKeys = Object.keys(dataMap).map(toNumber);
        if (dataKeys.length === 0) return Promise.resolve();
        var dataList = isRealtimeReport ? _this3.combineDelayValues(Object.values(dataMap)) : Object.values(dataMap);
        var requestOpts = {
          method: 'post',
          body: JSON.stringify(dataList)
        };
        return http(reportUrl, requestOpts, 'text').then(function () {
          _this3.tmpSenderSize = maxSendBufferSize;
          return _this3.idb.delete(logTable, dataKeys);
        }).catch(function (err) {
          // eslint-disable-next-line
          console.error(err); // if api max size limited then reduce 5 counts.

          var tmpSize = _this3.tmpSenderSize - 5;
          tmpSize = tmpSize < 1 ? 1 : tmpSize;
          _this3.tmpSenderSize = tmpSize;
        });
      });
    }
    /**
     *
     * @param {object} event The log event
     * @returns {promise} The fetch promise
     */

  }, {
    key: "sendDirect",
    value: function sendDirect(event) {
      var requestOpts = {
        method: 'post',
        body: JSON.stringify([this.combineEvent(event)])
      };
      var reportUrl = this.opts.reportUrl; // eslint-disable-next-line

      console.log('[Skynet] Debug: ', event); // eslint-disable-next-line

      return http(reportUrl, requestOpts, 'text').catch(console.error);
    }
    /**
     * Combine report value which in latency mode to support lazy load.
     *
     * @param {array} dataList The event list which should send
     * @returns {array} The combined list.
     */

  }, {
    key: "combineDelayValues",
    value: function combineDelayValues() {
      var dataList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      var getUid = this.opts.getUid;
      return map(dataList, function (event) {
        return _objectSpread(_objectSpread({}, event), {}, {
          u: getUid() || window.GA_UID
        });
      });
    }
    /**
     * Combine log event with default data
     *
     * @param {object} event The log event
     *
     * @returns {object} The combined event
     */

  }, {
    key: "combineEvent",
    value: function combineEvent() {
      var _this4 = this;

      var event = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var conn = navigator.connection;
      var _this$opts2 = this.opts,
          getGuid = _this$opts2.getGuid,
          getUid = _this$opts2.getUid;
      var extendFields = Object.keys(this.eventOpts).reduce(function (prev, cur) {
        var extendValue = _this4.eventOpts[cur];
        var realValue = isFunction(extendValue) ? extendValue() : extendValue;
        return _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, cur, realValue));
      }, {});
      return _objectSpread(_objectSpread({
        g: getGuid(),
        // combine userId in lazy mode
        u: getUid() || window.GA_UID,
        net: conn ? conn.rtt : -1,
        // eslint-disable-next-line
        w: conn ? conn.downlink > 10 ? 99 : conn.downlink : -1,
        // 防止出现异常数据
        dt: new Date().toISOString()
      }, extendFields), event);
    }
    /**
     * Log the event to store
     *
     * @param {object} event The log event
     */

  }, {
    key: "log",
    value: function log(event) {
      if (!this.tabActive || !this.initialized) return Promise.resolve();

      if (typeof window !== 'undefined' && window && window._BY_SKYNET_DEBUG_) {
        // eslint-disable-line
        return this.sendDirect(event);
      }

      if (this.isRealtimeReport) {
        return this.addDelayEvent(event);
      }

      return this.addEvent(event);
    }
    /**
     * Add event
     *
     * @param {object} event The log event
     */

  }, {
    key: "addEvent",
    value: function addEvent(event) {
      return this.idb.insert(this.opts.logTable, this.combineEvent(event)).catch();
    }
    /**
     * Add delay consumed realtime event
     *
     * @param {object} event The log event
     */

  }, {
    key: "addDelayEvent",
    value: function addDelayEvent(event) {
      var self = this;
      var reportDelay = this.opts.reportDelay;
      clearTimeout(self.delayTimer);
      self.delayTimer = setTimeout(function () {
        self.consume.apply(self);
      }, reportDelay);
      return this.addEvent(event);
    }
  }]);

  return ByLogger;
}();

export { ByLogger as default };